import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Popconfirm, Radio } from 'antd';
import {
    ArrowLeftOutlined,
    BankOutlined,
    CreditCardOutlined,
    DeleteOutlined,
    EditOutlined,
    ShoppingCartOutlined,
    UsergroupAddOutlined,
} from '@ant-design/icons';
import { Catcher, LoadingApiByIds, booleanYNText } from '../';
import cx from 'classnames';
import { useProfile, useRouterStore, useTariff } from '../../hooks';
import { IStepperFormData } from './Components/TariffCreationModal/hooks';
import {
    deleteTariff,
    getCompaniesChildrenSelect,
    getCountriesSelect,
    getNetworksSelect,
    getOperatorsSelect,
    getTariff,
} from '../../middleware';
import { getTariffPackagesList } from '../../middleware';
import { stringMoney, tAdvancedPlural } from '../../instruments';
import { tB, tF, tI, tP, tT } from '../../translate';
import { TariffCreationModal } from './Components';
import { AssignTariffModalNew } from './Components/AssignTariffModal/AssignTariffModalNew';
import { AssignTariffModal } from './Components/AssignTariffModal/AssignTatiffModal';
import { TariffItemAssignedCompanies } from './Components/TariffItem/TariffItemAssignedCompanies';
import { TariffItemPackageView } from './Components/TariffItem/TariffItemPackageView';
import { TariffItemAssignedCards } from './Components/TariffItem/TariffsItemAssignedCards';
import CardStyles from '../styles/m_itemCardStyles.less';
import TariffsStyles from './m_styles.less';

export const TariffItem: React.FC = observer(() => {
    const [isOpen, setIsOpen] = useState(false);
    const [isAssignOpen, setIsAssignOpen] = useState(false);
    const [currentView, setCurrentView] = useState<'packageView' | 'assigned' | 'cards'>('packageView');
    const companyId = useProfile().companyId;
    const tariff = useTariff();
    const routerStore = useRouterStore();
    const currentRouteId = routerStore.routerState.params.id;

    const { companyName, companies, cards, cardsCount, tariffPackages, price, outOfBundle, productId, planType } =
        tariff;

    const goTo = (event: React.MouseEvent): void => {
        if (!(event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)) {
            event.preventDefault();

            routerStore.goTo('TariffsList').then();
        }
    };

    const openStepper = () => {
        setIsOpen(true);
    };

    const closeStepper = () => {
        setIsOpen(false);
    };

    const openAssignStepper = () => {
        setIsAssignOpen(true);
    };

    const closeAssignStepper = () => {
        setIsAssignOpen(false);
    };

    const getInitialData = (): IStepperFormData | undefined => {
        const initialTariffLocalPackagesIds =
            tariff.tariffPackages?.filter((p) => p.packageType === 'local').map((p) => +p.id) || [];
        const initialTariffRoamingPackagesIds =
            tariff.tariffPackages?.filter((p) => p.packageType === 'roaming').map((p) => +p.id) || [];

        return {
            ...tariff,
            tariffPackageIds: [...initialTariffLocalPackagesIds, ...initialTariffRoamingPackagesIds],
            tariffLocalPackagesIds: initialTariffLocalPackagesIds,
            tariffRoamingPackagesIds: initialTariffRoamingPackagesIds,
            companyIds: companies?.map((c) => +c.id) || [],
            initialTariffLocalPackagesIds,
            initialTariffRoamingPackagesIds,
            companyId: tariff.companyId.id,
        };
    };

    const handleDeleteTariff = async () => {
        await deleteTariff(tariff.id).then(() => {
            routerStore.goTo('TariffsList').then();
        });
    };

    useEffect(() => {
        if (currentRouteId && companyId) {
            getTariff(currentRouteId).then();
            getTariffPackagesList().then();
            getCompaniesChildrenSelect(companyId).then();
            getOperatorsSelect().then();
            getNetworksSelect().then();
            getCountriesSelect().then();
        }
    }, [currentRouteId, companyId]);

    const views = {
        packageView: <TariffItemPackageView tariffPackages={tariffPackages} />,
        assigned: <TariffItemAssignedCompanies companies={companies} />,
        cards: <TariffItemAssignedCards cards={cards} />,
    };

    return (
        <Catcher>
            <div className={cx(CardStyles.main, TariffsStyles.container)}>
                <div className={TariffsStyles.tariffItemContainer}>
                    <Button onClick={(e) => goTo(e)} type={'ghost'}>
                        <ArrowLeftOutlined /> {tB('Back To Tariffs')}
                    </Button>

                    <div className={TariffsStyles.tariffItemHeaderContainer}>
                        <div>
                            <h1>{tariff.name}</h1>
                            <p>{tariff.description}</p>
                        </div>
                        <div className={TariffsStyles.tariffItemButtonsContainer}>
                            <Button onClick={openStepper}>
                                <EditOutlined /> {tP('Edit Tariff')}
                            </Button>
                            <Button type={'primary'} onClick={openAssignStepper}>
                                <ShoppingCartOutlined /> {tB('Buy Tariff')}
                            </Button>
                            <Popconfirm
                                placement="top"
                                title={tT('Are you sure you want to delete this tariff? This action cannot be undone.')}
                                onConfirm={handleDeleteTariff}
                                okText={tB('Yes')}
                                cancelText={tB('No')}
                            >
                                <Button type={'primary'} danger>
                                    <DeleteOutlined />
                                    {tB('Delete')}
                                </Button>
                            </Popconfirm>
                        </div>
                    </div>
                    <Divider />
                    <div className={TariffsStyles.tariffItemStartsContainer}>
                        <div className={TariffsStyles.tariffItemStats}>
                            <div className={TariffsStyles.tariffItemStatsTitle}>{tF('Price')}</div>
                            <div className={TariffsStyles.tariffItemStatsInfo}>{stringMoney(price, 'EUR')}</div>
                        </div>
                        <div className={TariffsStyles.tariffItemStats}>
                            <div className={TariffsStyles.tariffItemStatsTitle}>{tI('Out of Bundle')}</div>
                            <div className={TariffsStyles.tariffItemStatsInfo}>{booleanYNText(outOfBundle)}</div>
                        </div>
                        {productId && (
                            <div className={TariffsStyles.tariffItemStats}>
                                <div className={TariffsStyles.tariffItemStatsTitle}>{tF('Product ID')}</div>
                                <div className={TariffsStyles.tariffItemStatsInfo}>{productId}</div>
                            </div>
                        )}
                        {planType && (
                            <div className={TariffsStyles.tariffItemStats}>
                                <div className={TariffsStyles.tariffItemStatsTitle}>{tF('Plan Type')}</div>
                                <div className={TariffsStyles.tariffItemStatsInfo}>{planType}</div>
                            </div>
                        )}
                        <div className={TariffsStyles.tariffItemStats}>
                            <div className={TariffsStyles.tariffItemStatsTitle}>{tF('Created by')}</div>
                            <div className={TariffsStyles.tariffItemStatsInfo}>
                                <BankOutlined />
                                {companyName}
                            </div>
                        </div>
                        <div className={TariffsStyles.tariffItemStats}>
                            <div className={TariffsStyles.tariffItemStatsTitle}>{tF('Assigned Companies')}</div>
                            <div className={TariffsStyles.tariffItemStatsInfo}>
                                <UsergroupAddOutlined />
                                {tAdvancedPlural('company', companies?.length)}
                            </div>
                        </div>
                        <div className={TariffsStyles.tariffItemStats}>
                            <div className={TariffsStyles.tariffItemStatsTitle}>Active Cards</div>
                            <div className={TariffsStyles.tariffItemStatsInfo}>
                                <CreditCardOutlined />
                                {tAdvancedPlural('card', cardsCount)}
                            </div>
                        </div>
                    </div>
                    <div>
                        <Radio.Group
                            defaultValue="packageView"
                            buttonStyle="solid"
                            onChange={(e) => setCurrentView(e.target.value)}
                        >
                            <Radio.Button value="packageView">{tF('Packages')}</Radio.Button>
                            <Radio.Button value="assigned">{tF('Assigned Companies')}</Radio.Button>
                            <Radio.Button value="cards">{tF('Cards Using Tariff')}</Radio.Button>
                        </Radio.Group>
                    </div>
                    {views[currentView]}
                    <TariffCreationModal closeStepper={closeStepper} isOpen={isOpen} initialData={getInitialData()} />
                    <AssignTariffModalNew
                        isOpen={isAssignOpen}
                        closeStepper={closeAssignStepper}
                        onSuccess={() => getTariff(currentRouteId)}
                        tariff={tariff}
                    />
                    <LoadingApiByIds idSpinners={['GET_TARIFF']} />
                </div>
            </div>
        </Catcher>
    );
});
